/**
 * ComputedAttributeFormWizard.jsx
 * Formulario de Atributo Computado em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Descricao, Atributo, Tipo, Prioridade, Expressao (obrigatorio)
 * 2. Teste - Testar expressao com dispositivo (opcional)
 * 3. Revisao
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  createFilterOptions,
  Button,
  Snackbar,
} from '@mui/material';
import {
  Functions as ComputedIcon,
  Science as TestIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import usePositionAttributes from '../../common/attributes/usePositionAttributes';
import { useCatch } from '../../reactHelper';
import { snackBarDurationLongMs } from '../../common/util/duration';
import fetchOrThrow from '../../common/util/fetchOrThrow';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

const allowedProperties = ['valid', 'latitude', 'longitude', 'altitude', 'speed', 'course', 'address', 'accuracy'];

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const positionAttributes = usePositionAttributes(t);

  const options = Object.entries(positionAttributes)
    .filter(([key, value]) => !value.property || allowedProperties.includes(key))
    .map(([key, value]) => ({
      key,
      name: value.name,
      type: value.type,
    }));

  const filter = createFilterOptions({
    stringify: (option) => option.name,
  });

  const handleAttributeChange = (_, option) => {
    const attribute = option ? option.key || option.inputValue || option : null;
    if (option && (option.type || option.inputValue)) {
      updateData({ attribute, type: option.type });
    } else {
      updateData({ attribute });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedDescription')} *`}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            error={!!errors?.description}
            helperText={errors?.description}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            freeSolo
            value={options.find((option) => option.key === data.attribute) || data.attribute || null}
            onChange={handleAttributeChange}
            filterOptions={(opts, params) => {
              const filtered = filter(opts, params);
              if (params.inputValue && !opts.some((x) => (typeof x === 'object' ? x.key : x) === params.inputValue)) {
                filtered.push({ inputValue: params.inputValue, name: `${t('sharedAdd')} "${params.inputValue}"` });
              }
              return filtered;
            }}
            options={options}
            getOptionLabel={(option) => (typeof option === 'object' ? option.inputValue || option.name : option)}
            renderOption={(props, option) => <li {...props}>{option.name || option}</li>}
            renderInput={(params) => <TextField {...params} label={t('sharedAttribute')} className="premium-input" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" className="premium-input" disabled={data.attribute in positionAttributes}>
            <InputLabel>{t('sharedType')}</InputLabel>
            <Select
              label={t('sharedType')}
              value={data.type || ''}
              onChange={(e) => updateData({ type: e.target.value })}
            >
              <MenuItem value="string">{t('sharedTypeString')}</MenuItem>
              <MenuItem value="number">{t('sharedTypeNumber')}</MenuItem>
              <MenuItem value="boolean">{t('sharedTypeBoolean')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            label={t('sharedPriority')}
            value={data.priority || 0}
            onChange={(e) => updateData({ priority: Number(e.target.value) })}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={`${t('sharedExpression')} *`}
            value={data.expression || ''}
            onChange={(e) => updateData({ expression: e.target.value })}
            error={!!errors?.expression}
            helperText={errors?.expression}
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: TESTE
// ============================================================================

const TestStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const [deviceId, setDeviceId] = useState(null);
  const [result, setResult] = useState(null);

  const testAttribute = useCatch(async () => {
    const query = new URLSearchParams({ deviceId });
    const url = `/api/attributes/computed/test?${query.toString()}`;
    const response = await fetchOrThrow(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    setResult(await response.text());
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardTestExpressionHelp') || 'Selecione um dispositivo para testar a expressao.'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={deviceId}
            onChange={(e) => setDeviceId(Number(e.target.value))}
            endpoint="/api/devices"
            label={t('sharedDevice')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box display="flex" alignItems="center" height="100%">
            <Button
              variant="outlined"
              onClick={testAttribute}
              disabled={!deviceId}
              sx={{
                borderColor: colors.accent,
                color: colors.accent,
                '&:hover': {
                  backgroundColor: colors.accentSubtle,
                  borderColor: colors.accent,
                },
                '&.Mui-disabled': {
                  borderColor: colors.borderDefault,
                  color: colors.textMuted,
                },
              }}
            >
              {t('sharedTestExpression')}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={!!result}
        onClose={() => setResult(null)}
        autoHideDuration={snackBarDurationLongMs}
        message={result}
      />
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedDescription')} value={data.description} />
        <InfoRow label={t('sharedAttribute')} value={data.attribute} />
        <InfoRow label={t('sharedType')} value={data.type} />
        <InfoRow label={t('sharedPriority')} value={data.priority} />
        <InfoRow label={t('sharedExpression')} value={data.expression?.substring(0, 50) + (data.expression?.length > 50 ? '...' : '')} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ComputedAttributeFormWizard = ({ computedAttribute, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardComputedAttributeDescription') || 'Dados do atributo computado',
      icon: <ComputedIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.description?.trim()) errors.description = t('sharedRequired');
        if (!data.expression?.trim()) errors.expression = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedDescription'), value: data.description },
        { label: t('sharedAttribute'), value: data.attribute },
      ],
    },
    {
      label: t('sharedTest'),
      title: t('sharedTest'),
      description: t('wizardTestDescription') || 'Testar a expressao',
      icon: <TestIcon sx={{ fontSize: 18 }} />,
      component: TestStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = computedAttribute?.id ? `/api/attributes/computed/${computedAttribute.id}` : '/api/attributes/computed';
      const method = computedAttribute?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/attributes/computed');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/attributes/computed');
  };

  const initialData = computedAttribute || {
    description: '',
    attribute: '',
    type: '',
    priority: 0,
    expression: '',
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={computedAttribute?.id ? null : 'computed-attribute-wizard-draft'}
      completeButtonText={computedAttribute?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default ComputedAttributeFormWizard;
